import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import API from "../../api/axios"
import "./hextra.css"

/* ── Static data (company content — not from DB) ── */
const clients = [
  { icon: "fa fa-user-graduate",   label: "University Students",          desc: "Early exposure to financial markets." },
  { icon: "fa fa-briefcase",       label: "Young Professionals",          desc: "Alternate income and wealth-building skills." },
  { icon: "fa fa-store",           label: "Entrepreneurs",                desc: "Understanding cash flow and investment risk." },
  { icon: "fa fa-chart-line",      label: "Aspiring Traders",             desc: "Structured path from beginner to consistent." },
  { icon: "fa fa-piggy-bank",      label: "Investors",                    desc: "Better market awareness for portfolio decisions." },
  { icon: "fa fa-building",        label: "Corporate Teams",              desc: "Financial literacy workshops for employees." },
  { icon: "fa fa-users",           label: "Individuals Seeking Literacy", desc: "No prior experience needed." },
]

const advantages = [
  { icon: "fa fa-book-open",        text: "Modern and practical curriculum", sub: "No outdated theory." },
  { icon: "fa fa-broadcast-tower",  text: "Real-time market learning",       sub: "Learn what is happening now." },
  { icon: "fa fa-user-tie",         text: "Personalised mentorship",         sub: "Not a one-size-fits-all course." },
  { icon: "fa fa-seedling",         text: "Sustainable trading habits",      sub: "We discourage gambling approaches." },
  { icon: "fa fa-shield-alt",       text: "Discipline and risk emphasis",    sub: "The Rovers Access hallmark." },
  { icon: "fa fa-laptop",           text: "Technology-driven environment",   sub: "Accessible via mobile and desktop." },
]

const goals = [
  { n: "01", text: "Establish a leading financial academy in Africa with physical and digital presence." },
  { n: "02", text: "Build a robust online learning platform (LMS) with video, quizzes, and live trading rooms." },
  { n: "03", text: "Expand mentorship programmes regionally across East Africa and beyond." },
  { n: "04", text: "Create strategic partnerships in fintech, brokers, and financial media." },
  { n: "05", text: "Promote financial literacy among youth and professionals through free workshops and outreach." },
]

const csr = [
  { icon: "fa fa-university",    text: "Free financial literacy seminars in local universities." },
  { icon: "fa fa-hands-helping", text: "Youth mentorship combining entrepreneurship, digital skills, and responsible investing." },
  { icon: "fa fa-gift",          text: "Pro bono training for select community groups and small business owners." },
]

/* ── Level colour map ── */
const levelColor = {
  beginner:     "#c9a227",
  intermediate: "#2e7d52",
  advanced:     "#1a6b9a",
  all:          "#8b5cf6",
}

/* ── Dynamic Programmes section ── */
const ProgrammesSection = () => {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    API.get("/courses")
      .then(res => {
        const data = res.data.data || []
        // Show all published, free-trial first, then sorted by price
        const sorted = [...data].sort((a, b) => {
          if (a.isTrial && !b.isTrial) return -1
          if (!a.isTrial && b.isTrial) return 1
          return a.price - b.price
        })
        setCourses(sorted)
      })
      .catch(() => setCourses([]))
      .finally(() => setLoading(false))
  }, [])

  return (
    <section className="hextra-programs padding">
      <div className="container">
        <div className="hextra-header">
          <p className="hextra-eyebrow">OUR PROGRAMMES</p>
          <h2>Training Programmes</h2>
          <p className="hextra-sub">
            Choose the programme that fits your level and goals. Every programme is designed
            for real-world application from day one.
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#888" }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: 24, color: "#c9a227" }}></i>
          </div>
        ) : courses.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: "rgba(255,255,255,0.5)" }}>
            <p>Programmes coming soon.</p>
          </div>
        ) : (
          <div className="programs-grid">
            {courses.map((course, i) => {
              const color = levelColor[course.level] || "#c9a227"
              return (
                <div className="program-card" key={course.id} style={{ "--pcolor": color }}>
                  {/* Badges */}
                  {course.isTrial && (
                    <div className="program-trial-badge"><i className="fa fa-gift"></i> Free Trial</div>
                  )}
                  {course.isFeatured && !course.isTrial && (
                    <div className="program-popular-badge">⭐ Popular</div>
                  )}

                  <div className="program-top">
                    {course.duration && (
                      <span className="program-duration">
                        <i className="fa fa-clock"></i> {course.duration}
                      </span>
                    )}
                    <span className="program-level" style={{ color }}>
                      {course.level?.charAt(0).toUpperCase() + course.level?.slice(1)}
                    </span>
                  </div>

                  <h3>{course.title}</h3>
                  <p>{course.shortDesc || course.description?.slice(0, 110) + "…"}</p>

                  <div className="program-price">
                    {course.isFree || course.isTrial
                      ? <span className="program-price-free">FREE</span>
                      : <>
                          <span className="program-price-kes">KES</span>
                          <span className="program-price-val">{Number(course.price).toLocaleString()}</span>
                        </>
                    }
                  </div>

                  <Link to="/courses" className="program-btn">
                    View Course <i className="fa fa-arrow-right"></i>
                  </Link>
                </div>
              )
            })}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 28 }}>
          <Link to="/pricing" className="hextra-all-btn">
            <i className="fa fa-question-circle"></i> View FAQ
          </Link>
        </div>
      </div>
    </section>
  )
}

/* ── Main HExtra component ── */
const HExtra = () => {
  return (
    <>
      {/* 1 ── Target Clients */}
      <section className="hextra-clients padding">
        <div className="container">
          <div className="hextra-header">
            <p className="hextra-eyebrow">WHO WE SERVE</p>
            <h2>Our Target Clients</h2>
            <p className="hextra-sub">
              Rovers Access serves a diverse audience — from first-time market participants to
              seasoned professionals seeking structured, practical trading knowledge.
            </p>
          </div>
          <div className="clients-grid">
            {clients.map((c, i) => (
              <div className="client-card" key={i}>
                <div className="client-icon"><i className={c.icon}></i></div>
                <h4>{c.label}</h4>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2 ── Training Programmes — dynamic from API */}
      <ProgrammesSection />

      {/* 3 ── Competitive Advantage */}
      <section className="hextra-advantage padding">
        <div className="container">
          <div className="hextra-header">
            <p className="hextra-eyebrow">OUR EDGE</p>
            <h2>Our Competitive Advantage</h2>
          </div>
          <div className="advantage-grid">
            {advantages.map((a, i) => (
              <div className="advantage-card" key={i}>
                <div className="advantage-icon"><i className={a.icon}></i></div>
                <div>
                  <h4>{a.text}</h4>
                  <p>{a.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 ── Future Goals + CSR */}
      <section className="hextra-future padding">
        <div className="container">
          <div className="future-grid">
            <div className="future-col">
              <p className="hextra-eyebrow">WHERE WE'RE HEADED</p>
              <h2>Future Goals</h2>
              <p className="hextra-sub" style={{ textAlign: "left", maxWidth: "100%" }}>
                Rovers Access Limited is building towards becoming Africa's premier financial
                education institution.
              </p>
              <div className="goals-list">
                {goals.map((g, i) => (
                  <div className="goal-item" key={i}>
                    <div className="goal-num">{g.n}</div>
                    <p>{g.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="csr-col">
              <p className="hextra-eyebrow">GIVING BACK</p>
              <h2>Corporate Social Responsibility</h2>
              <p className="hextra-sub" style={{ textAlign: "left", maxWidth: "100%" }}>
                Rovers Access believes that financial knowledge is a form of empowerment.
                We invest in communities, not just clients.
              </p>
              <div className="csr-list">
                {csr.map((item, i) => (
                  <div className="csr-item" key={i}>
                    <div className="csr-icon"><i className={item.icon}></i></div>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="csr-banner">
                <i className="fa fa-heart"></i>
                <span>Financial knowledge is empowerment. We give it freely where it matters most.</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default HExtra
